import React, { useContext, useId, useState } from "react"
import { Field as FieldPrimitive } from "@base-ui/react/field"
import {
  ColorPickerStateContext,
  parseColor
} from "react-aria-components/ColorPicker"
import { useFieldContext } from "../field/field-context"
import { InputBase } from "../input-base"
import { cn } from "../utils"
import { VisuallyHidden } from "../visually-hidden"
import { getColorName } from "./get-color-name"
import { TRANSPARENT, toValue } from "./use-color-value"

// =============================================================================
// Styles
// =============================================================================

const hexStyles = [
  "w-full min-w-0 bg-transparent outline-none",
  "font-sans text-sm leading-6 text-gray-900"
]

// =============================================================================
// Helpers
// =============================================================================

const HEX = /^([0-9a-f]{3}|[0-9a-f]{6})$/i

// Accepts "#C0FFEE", "c0ffee" and pasted text with spaces around it. Not cut
// to six: a digit typed into a full value would shift the color it applies.
const normalise = (text: string) => text.replace(/#/g, "").trim()

// =============================================================================
// Props
// =============================================================================

export type ColorInputHexProps = {
  /** Names the input when it isn't inside a `Field` */
  accessibleName?: string
  /** Called when the input is clicked, so the field can open its picker */
  onClick?: () => void
  /** Additional CSS classes for layout */
  className?: string
}

// =============================================================================
// Component
// =============================================================================

/**
 * The hex text box. Built on `InputBase` rather than react-aria's `ColorField`
 * so it registers with `Field` (the label names and focuses it) and applies
 * the color on every valid keystroke, as the picker always has.
 *
 * Must be rendered inside a react-aria `ColorPicker`.
 */
export const ColorInputHex = React.forwardRef<
  HTMLInputElement,
  ColorInputHexProps
>(function ColorInputHex({ accessibleName, onClick, className }, ref) {
  const state = useContext(ColorPickerStateContext)
  // What the user is typing; `null` shows the committed color
  const [draft, setDraft] = useState<string | null>(null)
  const colorNameId = useId()
  const isInField = useFieldContext().labelId !== undefined

  if (!state) return null

  const committed = toValue(state.color)
  const isTransparent = committed === TRANSPARENT
  // The box already shows "transparent"
  const colorName = isTransparent ? undefined : getColorName(committed)
  const shown = draft ?? (isTransparent ? TRANSPARENT : committed.slice(1))

  const apply = (text: string) => {
    const next = normalise(text)
    setDraft(next)
    if (HEX.test(next)) state.setColor(parseColor(`#${next}`))
  }

  return (
    <div
      data-slot="color-input-hex"
      className={cn("flex flex-auto items-center gap-2", className)}
    >
      {shown !== TRANSPARENT && (
        <span aria-hidden className="text-sm leading-6 text-gray-700">
          #
        </span>
      )}
      <InputBase
        ref={ref}
        accessibleName={accessibleName}
        {...(colorName && !isInField
          ? { "aria-describedby": colorNameId }
          : {})}
        value={shown}
        spellCheck={false}
        autoComplete="off"
        maxLength={7}
        className={cn(hexStyles, shown !== TRANSPARENT && "uppercase")}
        onClick={onClick}
        onFocus={event => event.currentTarget.select()}
        onBlur={() => setDraft(null)}
        // A Menu around the panel jumps to an item on each typed letter and
        // swallows it, so typed characters stop here
        onKeyDown={event => {
          const typed =
            event.key.length === 1 &&
            !event.ctrlKey &&
            !event.metaKey &&
            !event.altKey
          if (typed) event.stopPropagation()
        }}
        onValueChange={apply}
        // Cleans the pasted text before inserting it at the selection, since
        // the 7-character cap would cut " #FB6068 " short
        onPaste={event => {
          event.preventDefault()
          const { value, selectionStart, selectionEnd } = event.currentTarget
          const pasted = normalise(event.clipboardData.getData("text"))
          apply(
            value.slice(0, selectionStart ?? value.length) +
              pasted +
              value.slice(selectionEnd ?? value.length)
          )
        }}
      />
      {/* The color in words. In a `Field` it registers as one of the field's
          descriptions: an `aria-describedby` here would replace the field's
          own description and error instead of adding to them */}
      {colorName &&
        (isInField ? (
          <FieldPrimitive.Description render={<span />} className="sr-only">
            {colorName}
          </FieldPrimitive.Description>
        ) : (
          <VisuallyHidden id={colorNameId}>{colorName}</VisuallyHidden>
        ))}
    </div>
  )
})

ColorInputHex.displayName = "ColorInput.Hex"
